import { localDayOf } from "../../shared/format/local-date.js";
import { responsiblePlhIdOfClient } from "./client-plh-lookup.js";
import type { RequestContext } from "../context/request-context.js";
import type { TechnicalReviewRepository } from "../../infrastructure/database/memory/in-memory-technical-review-repository.js";
import {
  TECHNICAL_DOCUMENT_TYPE_LABELS,
  TECHNICAL_REVIEW_HISTORY_LABELS,
  TECHNICAL_REVIEW_STATUS_LABELS,
  normalizeTechnicalReview,
  reviewLocksDocument,
  type TechnicalDocumentReview,
  type TechnicalDocumentSummary,
  type TechnicalDocumentType,
  type TechnicalReviewActor,
  type TechnicalReviewHistoryEntry,
  type TechnicalReviewStatus
} from "../../domain/review/technical-document-review.js";
import { canTransition, plhStatusOfReview, type PlhApprovalAction } from "../../domain/review/plh-approval-policy.js";
import { canActAsPlh, isEligibleToBeNamedPlh, isPlhQualifiedProfessional } from "../../domain/identity/plh-eligibility.js";
import { AppError, ErrorCode } from "../../shared/errors/app-error.js";
import { RbacAuthorizationService } from "../../permissions/authorization.js";
import { PermissionCode } from "../../permissions/permissions.js";
import type { AuditRecorder } from "../../audit/audit-recorder.js";
import { AuditAction } from "../../audit/audit-log.js";
import { notificationService } from "./notification-service.js";
import type { DocumentSnapshot } from "../../infrastructure/documents/document-engine.js";
import type { TechnicalCorrectionService } from "./technical-correction-service.js";
import { sectionLabelOf, validateCorrectionItems, type TechnicalCorrectionItemInput, type TechnicalCorrectionRequest } from "../../domain/review/technical-correction.js";
import { CLIENT_WITHOUT_PLH, type ProjectResponsibilityService } from "./project-responsibility-service.js";
import type { EngineeringWorkflowStatus } from "../../domain/review/engineering-workflow-status.js";
import type { DocumentHoldService } from "./document-hold-service.js";

const randomUUID = () => globalThis.crypto.randomUUID();

/**
 * O que cada módulo entrega ao mecanismo central: validar o próprio
 * documento, descrevê-lo para a fila e produzir o snapshot que o PLH analisa.
 * O documento nunca é copiado — só referenciado por tipo + id.
 */
export interface TechnicalDocumentAdapter {
  readonly documentType: TechnicalDocumentType;
  /** Validação específica do relatório (LP valida LP, ME valida ME…). */
  validate(context: RequestContext, documentId: string): Promise<{ valid: boolean; errors: string[] }>;
  summary(context: RequestContext, documentId: string): Promise<TechnicalDocumentSummary>;
  /** O DocumentModel do momento — Preview e PDF do PLH saem daqui. */
  buildSnapshot(context: RequestContext, documentId: string): Promise<DocumentSnapshot>;
  /** Status do documento no módulo ("DRAFT", "VALIDATED", "SIGNED"…). */
  documentStatus(context: RequestContext, documentId: string): Promise<string>;
  /**
   * Opcional: o módulo fecha o documento quando o PLH aprova, sem exigir um
   * segundo "FINALIZAR" do usuário. Só os tipos que implementam mudam de
   * comportamento — os demais seguem com o fluxo de finalização próprio.
   */
  onApproved?(context: RequestContext, documentId: string, approvedSnapshot?: unknown): Promise<void>;
  /**
   * APROVAR = FINALIZAR para este tipo de documento (caso do ME).
   *
   * Quando verdadeiro, a aprovação do PLH grava `finalizedAt`/`finalizedBy`
   * na mesma operação e o botão FINALIZAR RELATÓRIO deixa de aparecer — não
   * existe estado intermediário entre aprovado e finalizado. Sem isto, o ME
   * fechava no módulo (status FINALIZADO) mas o ciclo de revisão continuava
   * sem `finalizedAt`, e a tela de análise ainda oferecia um segundo
   * FINALIZAR que não tinha o que fazer. Tipos que mantêm a finalização como
   * ato separado (AVR, LP…) simplesmente não declaram isto.
   */
  readonly finalizesOnApproval?: boolean;
}

/**
 * Módulos que já têm o seu próprio ciclo (RI, AVR) entram na fila unificada
 * por leitura, sem segundo estado: o mecanismo central só os lista.
 */
export interface ExternalReviewSource {
  readonly documentType: TechnicalDocumentType;
  listQueue(context: RequestContext): Promise<TechnicalReviewQueueRow[]>;
}

export interface TechnicalReviewQueueRow {
  reviewId: string | null;
  documentType: TechnicalDocumentType;
  typeLabel: string;
  documentId: string;
  documentNumber: string;
  revision: string;
  clientId: string;
  client: string;
  project: string;
  unitArea: string;
  tag: string;
  osNumber: string;
  subOsNumber: string;
  elaboratedBy: string;
  /** ENGENHEIRO RESPONSÁVEL do Projeto (congelado no envio). */
  engineerProfessionalId: string | null;
  engineerName: string;
  finalizedAt: string | null;
  /** Inspetor responsável pelo documento (pode diferir de quem enviou). */
  inspectorName: string;
  inspectorProfessionalId: string | null;
  equipmentType: string;
  inspectionDate: string;
  /** Quantas vezes foi enviado (>1 = reenviado depois de correção). */
  cycle: number;
  correctedAt: string | null;
  signedAt: string | null;
  plhProfessionalId: string | null;
  plhName: string;
  submittedAt: string;
  status: TechnicalReviewStatus;
  statusLabel: string;
  /** Rota do workspace de análise ("/engineering/review/<id>" ou o workspace próprio do módulo). */
  reviewRoute: string;
  /** Rota da página do documento. */
  documentRoute: string;
  /** Fontes externas (RI, AVR) informam o estado padronizado diretamente; o ciclo central deriva do status/ciclo. */
  workflowStatus?: EngineeringWorkflowStatus;
}

export interface TechnicalReviewQueueFilters {
  documentType?: TechnicalDocumentType | "ALL";
  plhProfessionalId?: string;
  clientId?: string;
  project?: string;
  unitArea?: string;
  status?: TechnicalReviewStatus | "ALL";
  from?: string;
  to?: string;
  search?: string;
}

/** O que o módulo do documento consulta antes de deixar editar/assinar. */
export interface DocumentReviewState {
  review: TechnicalDocumentReview | null;
  locked: boolean;
  approved: boolean;
  reason: string;
}

/**
 * Ganchos que os módulos usam: estado (bloqueio), "correção executada" ao
 * salvar depois da devolução e "assinado" depois da aprovação.
 */
export interface DocumentReviewHooks {
  state(documentType: TechnicalDocumentType, documentId: string): Promise<DocumentReviewState>;
  correctionSaved(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<void>;
  signed(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<void>;
}

/**
 * Serviço central de revisão/aprovação PLH. Um ciclo por revisão do
 * documento; correção não cria documento nem revisão nova — reabre o mesmo
 * ciclo (reviewId preservado, cycle + 1).
 */
export class TechnicalReviewService {
  private readonly adapters = new Map<TechnicalDocumentType, TechnicalDocumentAdapter>();

  /**
   * FILA POR DOCUMENTO — uma escrita de cada vez sobre a mesma revisão.
   *
   * Sem isto, dois cliques rápidos (ou duas abas) em APROVAR disparavam duas
   * chamadas que liam o estado ANTES de qualquer uma gravar. As duas viam
   * `AWAITING_PLH_REVIEW`, as duas passavam pela conferência de transição e
   * as duas aprovavam: duas entradas no histórico, dois registros de
   * auditoria e — nos tipos em que aprovar é finalizar (ME) — duas
   * finalizações com carimbos de hora diferentes.
   *
   * `startReview` já era idempotente por uma guarda explícita; `approve`
   * não era, e ainda PULA a conferência de transição quando o estado é
   * `AWAITING_PLH_REVIEW` (o PLH que abre e aprova no mesmo ato), de modo
   * que a segunda chamada concorrente não encontrava obstáculo nenhum.
   *
   * A fila transforma o caso CONCORRENTE no caso SEQUENCIAL, que já estava
   * correto: a segunda chamada só começa depois de a primeira gravar, relê
   * o estado novo e é recusada pela própria política. Não é substituto de
   * transação no banco — é a garantia possível num processo só, e é onde o
   * app roda hoje.
   */
  private readonly inFlight = new Map<string, Promise<unknown>>();

  /** Serializa as escritas de uma mesma revisão, preservando erro e retorno. */
  private exclusive<T>(reviewId: string, run: () => Promise<T>): Promise<T> {
    const previous = this.inFlight.get(reviewId) ?? Promise.resolve();
    // `then(run, run)` roda a próxima da fila tendo a anterior dado certo ou
    // errado: a falha de uma não pode travar a fila das demais.
    const current = previous.then(run, run);
    // O elo guardado é a versão silenciada. O erro de `current` continua
    // indo para quem chamou; se guardássemos `current`, a rejeição ficaria
    // sem tratamento e derrubaria a próxima.
    const link = current.then(() => undefined, () => undefined);
    this.inFlight.set(reviewId, link);
    void link.then(() => {
      // Só apaga se ninguém entrou na fila depois — senão removeria o elo
      // que a chamada seguinte está esperando.
      if (this.inFlight.get(reviewId) === link) this.inFlight.delete(reviewId);
    });
    return current;
  }
  private readonly externalSources: ExternalReviewSource[] = [];
  /** Correções por item (Painel de Engenharia) — opcional; sem ele a devolução segue só com motivo/seção. */
  private corrections: TechnicalCorrectionService | null = null;

  attachCorrectionService(service: TechnicalCorrectionService): void {
    this.corrections = service;
  }

  /** Responsáveis pelo Projeto (fonte de verdade do PLH e do Engenheiro) e HOLD (gate da finalização). */
  private responsibility: ProjectResponsibilityService | null = null;
  private holds: DocumentHoldService | null = null;

  attachResponsibility(service: ProjectResponsibilityService): void {
    this.responsibility = service;
  }

  /**
   * INSPECT IA REVIEW — revisão IA obrigatória antes de ENVIAR PARA ANÁLISE
   * (crítico bloqueia; técnico exige corrigir ou justificar; revisão
   * desatualizada exige nova). Ligado nas duas composições.
   */
  private submissionGate: ((context: RequestContext, documentType: TechnicalDocumentType, documentId: string) => Promise<void>) | null = null;

  attachSubmissionGate(gate: (context: RequestContext, documentType: TechnicalDocumentType, documentId: string) => Promise<void>): void {
    this.submissionGate = gate;
  }

  attachHoldService(service: DocumentHoldService): void {
    this.holds = service;
  }

  /** PLH do documento resolvido pelo Projeto — o que o modal "ENVIAR PARA O PLH" mostra (somente leitura). */
  async resolvePlhFor(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<{ plh: { professionalId: string; name: string; functionTitle: string; credential: string } | null; engineer: { professionalId: string; name: string } | null; project: { id: string; code: string; name: string } | null; reason: string }> {
    const summary = await this.adapterFor(documentType).summary(context, documentId);
    if (!this.responsibility) return { plh: null, engineer: null, project: null, reason: "Resolução de responsáveis pelo Projeto não configurada." };
    const [plh, eng] = await Promise.all([this.responsibility.resolveProjectPlh(context, summary.projectId ?? null), this.responsibility.resolveProjectEngineer(context, summary.projectId ?? null)]);
    const project = plh.project || eng.project;
    return {
      plh: plh.responsible && !plh.reason ? { professionalId: plh.responsible.professionalId, name: plh.responsible.name, functionTitle: plh.responsible.functionTitle, credential: plh.responsible.credential } : null,
      engineer: eng.responsible ? { professionalId: eng.responsible.professionalId, name: eng.responsible.name } : null,
      project: project ? { id: project.id, code: project.code, name: project.name } : null,
      reason: plh.reason
    };
  }

  get correctionService(): TechnicalCorrectionService | null {
    return this.corrections;
  }

  constructor(
    private readonly repo: TechnicalReviewRepository,
    private readonly identityRepo?: any,
    private readonly audit?: AuditRecorder
  ) {}

  /** Gate da M21: valida uso de instrumentos antes de efetivar PLH_APPROVED. */
  private ndtAssetApprovalGate: ((context: RequestContext, type: TechnicalDocumentType, documentId: string) => Promise<void>) | null = null;

  attachNdtAssetApprovalGate(gate: (context: RequestContext, type: TechnicalDocumentType, documentId: string) => Promise<void>): void {
    this.ndtAssetApprovalGate = gate;
  }

  registerAdapter(adapter: TechnicalDocumentAdapter): void {
    this.adapters.set(adapter.documentType, adapter);
  }

  registerExternalSource(source: ExternalReviewSource): void {
    this.externalSources.push(source);
  }

  supportedTypes(): TechnicalDocumentType[] {
    return Array.from(this.adapters.keys());
  }

  private get auth() { return new RbacAuthorizationService(); }

  private adapterFor(documentType: TechnicalDocumentType): TechnicalDocumentAdapter {
    const adapter = this.adapters.get(documentType);
    if (!adapter) {
      throw new AppError(ErrorCode.Validation, `No review adapter for ${documentType}`, { safeMessage: `O tipo de documento ${TECHNICAL_DOCUMENT_TYPE_LABELS[documentType] || documentType} ainda não possui módulo de elaboração integrado à revisão PLH.` });
    }
    return adapter;
  }

  // ------------------------------------------------------------- READ

  /**
   * A transição é permitida pela POLÍTICA TRANSVERSAL de aprovação?
   *
   * Existe para que a matriz de ações viva num lugar só
   * (`domain/review/plh-approval-policy.ts`) e valha igualmente para
   * Integridade, END e AVR. Antes cada método tinha a sua lista de status
   * aceitos, e listas paralelas divergem sem quebrar teste nenhum.
   */
  private assertTransition(
    review: { status: TechnicalReviewStatus; correctionRequestedAt?: string | null },
    action: PlhApprovalAction
  ): void {
    const status = plhStatusOfReview(review);
    const decision = canTransition(status, "PLH", action);
    if (!decision.allowed) {
      throw new AppError(ErrorCode.Conflict, `Transition ${action} not allowed from ${status}`, { safeMessage: decision.reason });
    }
  }

  async getReview(context: RequestContext, id: string): Promise<TechnicalDocumentReview | null> {
    const review = await this.repo.findById(context, id);
    return review ? normalizeTechnicalReview(review) : null;
  }

  /**
   * Revisões do Cliente ativo com o `history` completo — a fonte da
   * ATIVIDADE RECENTE do Painel de Engenharia.
   *
   * `listQueue` não serve: ela projeta `TechnicalReviewQueueRow`, que não
   * carrega o histórico. E a trilha de auditoria também não serve — no app
   * real ela é descartada (`mockAudit`, CLAUDE.md §17), então um feed
   * montado sobre ela viria sempre vazio.
   *
   * O recorte por Cliente ativo é o MESMO de `listQueue`: o Painel não pode
   * mostrar na atividade um documento que não mostra na fila.
   */
  async listReviewsWithHistory(context: RequestContext): Promise<TechnicalDocumentReview[]> {
    this.auth.require(context, PermissionCode.TechnicalReviewViewQueue);
    const reviews = (await this.repo.listAll(context)).map(normalizeTechnicalReview);
    if (!context.activeClientId) return reviews;
    const active = String(context.activeClientId);
    return reviews.filter(review => String(review.summary.clientId ?? "") === active);
  }

  /** O ciclo mais recente do documento — a página de elaboração decide botão/bloqueio por ele. */
  async getDocumentReview(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<TechnicalDocumentReview | null> {
    const review = await this.repo.findLatestByDocument(context, documentType, documentId);
    return review ? normalizeTechnicalReview(review) : null;
  }

  /** Ganchos para os módulos (LP, ME, PM…): consultar bloqueio, marcar correção e assinatura. */
  hooks(): DocumentReviewHooks {
    return {
      state: (documentType, documentId) => this.documentState({} as RequestContext, documentType, documentId),
      correctionSaved: (context, documentType, documentId) => this.markCorrectionDone(context, documentType, documentId),
      signed: (context, documentType, documentId) => this.markSigned(context, documentType, documentId)
    };
  }

  async documentState(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<DocumentReviewState> {
    const review = await this.getDocumentReview(context, documentType, documentId);
    if (!review) return { review: null, locked: false, approved: false, reason: "" };
    const locked = reviewLocksDocument(review.status);
    return {
      review,
      locked,
      approved: review.status === "PLH_APPROVED",
      reason: locked ? `Documento em "${TECHNICAL_REVIEW_STATUS_LABELS[review.status]}" (PLH: ${review.assignedPlhName}). A edição técnica está bloqueada; alterações só pelo fluxo de correção.` : ""
    };
  }

  /** PLHs habilitados pelo gate real para o Cliente — a única lista para o select. */
  async listPlhCandidates(context: RequestContext, clientId: string): Promise<Array<{ id: string; name: string; functionTitle: string; credential: string; userId: string | null }>> {
    this.auth.require(context, PermissionCode.TechnicalReviewSubmitToPlh);
    if (!this.identityRepo?.listProfessionals) return [];
    const all: any[] = await this.identityRepo.listProfessionals();
    return all
      // Seletor de candidatos: quem PODE ser nomeado PLH.
      .filter(p => isEligibleToBeNamedPlh(p).allowed)
      .map(p => ({ id: String(p.id), name: p.name || "", functionTitle: p.functionTitle || "", credential: p.crea || p.cft || "", userId: p.userId ? String(p.userId) : null }))
      .sort((a, b) => a.name.localeCompare(b.name, "pt-BR"));
  }

  // ------------------------------------------------------------ SUBMIT

  /** Validação específica do módulo, executada antes de abrir o modal e de novo no envio. */
  async validateForSubmission(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<{ valid: boolean; errors: string[] }> {
    return this.adapterFor(documentType).validate(context, documentId);
  }

  async submit(context: RequestContext, documentType: TechnicalDocumentType, documentId: string, input: { plhProfessionalId?: string; note?: string } = {}): Promise<TechnicalDocumentReview> {
    this.auth.require(context, PermissionCode.TechnicalReviewSubmitToPlh);
    const adapter = this.adapterFor(documentType);
    const docStatus = await adapter.documentStatus(context, documentId);
    if (docStatus === "SIGNED") {
      throw new AppError(ErrorCode.Conflict, "Signed document", { safeMessage: "Documento assinado não é enviado ao PLH." });
    }
    const existing = await this.getDocumentReview(context, documentType, documentId);
    if (existing && existing.status !== "CORRECTION_REQUESTED") {
      throw new AppError(ErrorCode.Conflict, "Document already under review", { safeMessage: `O documento já está em "${TECHNICAL_REVIEW_STATUS_LABELS[existing.status]}".` });
    }
    const validation = await adapter.validate(context, documentId);
    if (!validation.valid) {
      throw new AppError(ErrorCode.Validation, "Document not ready for PLH", { safeMessage: `O documento não pode ser enviado ao PLH: ${validation.errors.join(" ")}` });
    }
    if (this.submissionGate) await this.submissionGate(context, documentType, documentId);
    const summary = await adapter.summary(context, documentId);
    // PLH RESPONSÁVEL vem do CLIENTE do Projeto (fonte de verdade); sem resolução configurada, aceita o id informado (legado).
    let responsibility: TechnicalDocumentReview["responsibility"] = null;
    let plhId = (input.plhProfessionalId || "").trim();
    if (this.responsibility) {
      const resolved = await this.responsibility.assertProjectHasValidPlh(context, summary.projectId ?? null);
      plhId = resolved.professionalId;
      responsibility = await this.responsibility.snapshotOf(context, summary.projectId ?? null, summary.clientId);
    }
    if (!plhId) throw new AppError(ErrorCode.Validation, "PLH not selected", { safeMessage: CLIENT_WITHOUT_PLH });
    const plh = this.identityRepo ? await this.identityRepo.getProfessional(plhId) : null;
    if (!plh) throw new AppError(ErrorCode.Validation, "PLH not found", { safeMessage: "O PLH responsável não foi encontrado no cadastro." });
    const gate = isPlhQualifiedProfessional(plh, await responsiblePlhIdOfClient(this.identityRepo as never, String(summary.clientId)));
    if (!gate.allowed) {
      throw new AppError(ErrorCode.Validation, "Not a PLH", { safeMessage: `O PLH responsável não está habilitado para este Cliente: ${gate.reason || ""}` });
    }
    const snapshot = await adapter.buildSnapshot(context, documentId);
    const now = new Date().toISOString();
    const actor = await this.actorOf(context);
    const resubmission = !!existing;
    const review: TechnicalDocumentReview = existing
      ? {
        ...existing,
        cycle: existing.cycle + 1,
        summary,
        revision: summary.revision,
        submittedBy: actor,
        submittedAt: now,
        submissionNote: (input.note || "").trim(),
        assignedPlhProfessionalId: String(plh.id),
        assignedPlhName: plh.name || "",
        status: "AWAITING_PLH_REVIEW",
        reviewStartedAt: null,
        reviewedAt: null,
        submissionSnapshot: snapshot.data,
        responsibility,
        finalizedAt: null,
        finalizedBy: null,
        updatedAt: now
      }
      : {
        id: `rev-${randomUUID()}`,
        documentType,
        documentId,
        revision: summary.revision,
        summary,
        cycle: 1,
        submittedBy: actor,
        submittedAt: now,
        submissionNote: (input.note || "").trim(),
        assignedPlhProfessionalId: String(plh.id),
        assignedPlhName: plh.name || "",
        status: "AWAITING_PLH_REVIEW",
        reviewStartedAt: null,
        reviewedAt: null,
        correctionReason: "",
        correctionSection: "",
        correctionRequestedAt: null,
        correctedAt: null,
        reviewComment: "",
        approvedAt: null,
        approvedBy: null,
        submissionSnapshot: snapshot.data,
        responsibility,
        finalizedAt: null,
        finalizedBy: null,
        approvalSnapshot: null,
        signedAt: null,
        history: [],
        createdAt: now,
        updatedAt: now
      };
    review.history = [...review.history, {
      at: now, by: actor.userId, action: resubmission ? "RESUBMITTED" : "SUBMITTED",
      detail: `${resubmission ? "Reenviado" : "Enviado"} ao PLH ${plh.name} por ${actor.name}.${review.submissionNote ? ` Observação: ${review.submissionNote}` : ""}`,
      fromStatus: existing?.status ?? null, toStatus: "AWAITING_PLH_REVIEW", professionalId: actor.professionalId
    }];
    await this.repo.save(context, review);
    await this.recordAudit(context, resubmission ? AuditAction.DocumentResubmittedToPlh : AuditAction.DocumentSubmittedToPlh, review, existing?.status ?? null, { plhProfessionalId: String(plh.id), note: review.submissionNote });
    await this.notify(plh.userId, "DOCUMENT_SUBMITTED_TO_PLH", `${TECHNICAL_DOCUMENT_TYPE_LABELS[documentType]} aguardando sua análise`, `${summary.documentNumber} (${summary.tag}) foi ${resubmission ? "reenviado" : "enviado"} por ${actor.name} para análise do PLH.`, review, `/engineering/review/${review.id}`, `TR_SUBMIT_${review.id}_${review.cycle}`);
    return review;
  }

  // ------------------------------------------------------------- PLH

  /** Enfileirado por documento: ver `exclusive`. A regra vive em `startReviewLocked`. */
  async startReview(context: RequestContext, reviewId: string): Promise<TechnicalDocumentReview> {
    return this.exclusive(reviewId, () => this.startReviewLocked(context, reviewId));
  }

  private async startReviewLocked(context: RequestContext, reviewId: string): Promise<TechnicalDocumentReview> {
    this.auth.require(context, PermissionCode.TechnicalReviewReview);
    const review = await this.requireReview(context, reviewId);
    if (review.status === "PLH_REVIEWING") return review;
    // Quem decide se a transição é possível é a POLÍTICA TRANSVERSAL, não uma
    // condição escrita aqui: a mesma matriz vale para Integridade, END e AVR.
    this.assertTransition(review, "START_ANALYSIS");
    const { professional } = await this.requireAssignedPlh(context, review);
    const now = new Date().toISOString();
    const updated: TechnicalDocumentReview = { ...review, status: "PLH_REVIEWING", reviewStartedAt: now, updatedAt: now, history: [...review.history, { at: now, by: context.userId || "system", action: "REVIEW_STARTED", detail: `Análise iniciada pelo PLH ${professional.name}.`, fromStatus: review.status, toStatus: "PLH_REVIEWING", professionalId: String(professional.id) }] };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.DocumentPlhReviewStarted, updated, review.status, { plhProfessionalId: String(professional.id) });
    return updated;
  }

  /** Enfileirado por documento: ver `exclusive`. A regra vive em `requestCorrectionLocked`. */
  async requestCorrection(context: RequestContext, reviewId: string, input: { reason?: string; section?: string; items?: TechnicalCorrectionItemInput[] }): Promise<TechnicalDocumentReview> {
    return this.exclusive(reviewId, () => this.requestCorrectionLocked(context, reviewId, input));
  }

  private async requestCorrectionLocked(context: RequestContext, reviewId: string, input: { reason?: string; section?: string; items?: TechnicalCorrectionItemInput[] }): Promise<TechnicalDocumentReview> {
    this.auth.require(context, PermissionCode.TechnicalReviewRequestCorrection);
    const review = await this.requireReview(context, reviewId);
    // MANDAR PARA CORREÇÃO é POR ITEM: cada linha exige SEÇÃO + ITEM/CAMPO +
    // OBSERVAÇÃO e vira uma pendência com destino exato para o Inspetor
    // responsável. Um motivo genérico sem itens não é aceito. O motivo geral
    // (quando não informado) é o resumo das linhas.
    const items = (input.items || []).filter(i => i && (i.sectionId || i.comment));
    const errors = validateCorrectionItems(items, { requireItem: true });
    if (errors.length > 0) throw new AppError(ErrorCode.Validation, "Invalid correction items", { safeMessage: errors.join(" ") });
    const reason = (input.reason || "").trim() || items.map((i, n) => `${String(n + 1).padStart(2, "0")} ${i.sectionLabel || sectionLabelOf(review.documentType, i.sectionId)}${i.itemId ? ` · ${i.itemLabel || i.itemId}` : ""}: ${(i.comment || "").trim()}`).join(" | ");
    if (!["AWAITING_PLH_REVIEW", "PLH_REVIEWING", "PLH_APPROVED"].includes(review.status)) {
      throw new AppError(ErrorCode.Conflict, "Not under review", { safeMessage: `Não é possível solicitar correção no status "${TECHNICAL_REVIEW_STATUS_LABELS[review.status]}".` });
    }
    const { professional } = await this.requireAssignedPlh(context, review);
    const now = new Date().toISOString();
    const updated: TechnicalDocumentReview = {
      ...review, status: "CORRECTION_REQUESTED", correctionReason: reason, correctionSection: (input.section || "").trim(), correctionRequestedAt: now, correctedAt: null, reviewedAt: now,
      approvedAt: null, approvedBy: null, approvalSnapshot: null, updatedAt: now,
      history: [...review.history, { at: now, by: context.userId || "system", action: "CORRECTION_REQUESTED", detail: `Motivo: ${reason}${input.section?.trim() ? ` · Seção: ${input.section.trim()}` : ""}`, fromStatus: review.status, toStatus: "CORRECTION_REQUESTED", professionalId: String(professional.id) }]
    };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.DocumentPlhCorrectionRequested, updated, review.status, { reason, section: updated.correctionSection, plhProfessionalId: String(professional.id), items: items.length });
    if (items.length > 0 && this.corrections) {
      // Itens com destino exato + notificação por item ao Inspetor responsável (deep link). Substitui a notificação genérica.
      const recipient = await this.corrections.recipientForProfessional(review.summary.inspectorProfessionalId ?? null, review.summary.elaboratedBy || review.submittedBy.name);
      const assignedTo = recipient.userId || recipient.professionalId ? recipient : { professionalId: review.submittedBy.professionalId, userId: review.submittedBy.userId, name: review.submittedBy.name };
      await this.corrections.requestCorrections(context, {
        documentType: review.documentType, documentId: review.documentId, documentNumber: review.summary.documentNumber, revision: review.revision, clientId: review.summary.clientId,
        documentRoute: review.summary.route, reviewId: review.id, assignedTo, items
      });
    } else {
      await this.notify(await this.userIdOfActor(review.submittedBy), "DOCUMENT_PLH_CORRECTION_REQUESTED", "Correção solicitada pelo PLH", `${review.summary.documentNumber} (${review.summary.tag}) voltou para correção. Motivo: ${reason}`, updated, review.summary.route, `TR_CORR_${review.id}_${now}`);
    }
    return updated;
  }

  /** Itens de correção do documento (pendentes e resolvidos), quando o serviço de correções está acoplado. */
  async correctionItems(context: RequestContext, review: TechnicalDocumentReview): Promise<readonly TechnicalCorrectionRequest[]> {
    if (!this.corrections) return [];
    return this.corrections.listByDocument(context, review.documentType, review.documentId);
  }

  /** O módulo chama quando o elaborador salva depois da devolução — "Correção executada" na timeline. */
  async markCorrectionDone(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<void> {
    const review = await this.getDocumentReview(context, documentType, documentId);
    if (!review || review.status !== "CORRECTION_REQUESTED" || review.correctedAt) return;
    const now = new Date().toISOString();
    await this.repo.save(context, { ...review, correctedAt: now, updatedAt: now, history: [...review.history, { at: now, by: context.userId || "system", action: "CORRECTION_DONE", detail: "Correção executada pelo elaborador." }] });
  }

  /** Enfileirado por documento: ver `exclusive`. A regra vive em `approveLocked`. */
  async approve(context: RequestContext, reviewId: string, input: { comment?: string } = {}): Promise<TechnicalDocumentReview> {
    return this.exclusive(reviewId, () => this.approveLocked(context, reviewId, input));
  }

  private async approveLocked(context: RequestContext, reviewId: string, input: { comment?: string } = {}): Promise<TechnicalDocumentReview> {
    this.auth.require(context, PermissionCode.TechnicalReviewApprove);
    const review = await this.requireReview(context, reviewId);
    // Aprovar direto de AGUARDANDO continua valendo (o PLH que abre e aprova
    // no mesmo ato), e é a política que diz isso — não uma lista local.
    if (review.status !== "AWAITING_PLH_REVIEW") this.assertTransition(review, "APPROVE");
    const { professional } = await this.requireAssignedPlh(context, review);
    if (this.ndtAssetApprovalGate) {
      await this.ndtAssetApprovalGate(context, review.documentType, review.documentId);
    }
    const now = new Date().toISOString();
    const comment = (input.comment || "").trim();
    const adapter = this.adapters.get(review.documentType);
    // Para os tipos em que aprovar já é finalizar (ME), o congelamento é
    // gravado na MESMA operação da aprovação: um só ato, um só registro.
    const closesNow = adapter?.finalizesOnApproval === true;
    const approver: TechnicalReviewActor = { userId: context.userId || "system", professionalId: String(professional.id), name: professional.name || "" };
    const updated: TechnicalDocumentReview = {
      ...review, status: "PLH_APPROVED", reviewStartedAt: review.reviewStartedAt || now, reviewedAt: now, approvedAt: now, reviewComment: comment,
      approvedBy: approver,
      approvalSnapshot: { documentType: review.documentType, documentId: review.documentId, revision: review.revision, model: review.submissionSnapshot, plhProfessionalId: String(professional.id), plhName: professional.name || "", comment, approvedAt: now },
      ...(closesNow ? { finalizedAt: now, finalizedBy: approver } : {}),
      updatedAt: now,
      history: [...review.history,
        { at: now, by: context.userId || "system", action: "APPROVED", detail: `Aprovado pelo PLH ${professional.name} (Rev. ${review.revision}).${comment ? ` Parecer: ${comment}` : ""}`, fromStatus: review.status, toStatus: "PLH_APPROVED", professionalId: String(professional.id) },
        ...(closesNow ? [{ at: now, by: context.userId || "system", action: "FINALIZED" as const, detail: `Relatório finalizado pela aprovação do PLH ${professional.name} (Rev. ${review.revision}). Revisão e signatários congelados.`, professionalId: String(professional.id) }] : [])
      ]
    };
    await this.repo.save(context, updated);
    // A aprovação do PLH fecha o documento no módulo que assim o define
    // (ME): não há um segundo "FINALIZAR". A falha aqui não pode desfazer
    // uma aprovação já gravada e auditada — é registrada e segue.
    try {
      // O módulo recebe o documento COMO FOI APROVADO, para congelá-lo — e
      // não uma remontagem a partir dos cadastros do momento da geração.
      await adapter?.onApproved?.(context, review.documentId, review.submissionSnapshot ?? null);
    } catch (error) {
      console.warn(`[REVISÃO TÉCNICA] Aprovação registrada, mas o módulo ${review.documentType} não pôde fechar o documento:`, error);
    }
    await this.recordAudit(context, AuditAction.DocumentPlhApproved, updated, review.status, {
      plhProfessionalId: String(professional.id), comment, revision: review.revision,
      documentType: review.documentType, documentId: review.documentId, clientId: String(review.summary.clientId),
      approvedAt: now, previousStatus: review.status, finalStatus: updated.status, finalized: closesNow
    });
    if (closesNow) {
      await this.recordAudit(context, AuditAction.DocumentFinalized, updated, review.status, { finalizedBy: approver.userId, byApproval: true, signed: !!review.signedAt, revision: review.revision });
    }
    await this.notify(await this.userIdOfActor(review.submittedBy), "DOCUMENT_PLH_APPROVED", "Documento aprovado pelo PLH", `${review.summary.documentNumber} (${review.summary.tag}) foi aprovado pelo PLH ${professional.name}.`, updated, review.summary.route, `TR_APPR_${review.id}_${now}`);
    return updated;
  }

  /**
   * FINALIZAR RELATÓRIO (END): só depois de APROVADO pelo PLH, sem correção
   * pendente e fora de HOLD. Gera o snapshot final (o aprovado), bloqueia a
   * edição ordinária (o status PLH_APPROVED já bloqueia) e audita. Não
   * assina: FINALIZADO — AGUARDANDO ASSINATURA até a assinatura do módulo.
   */
  async finalizationGate(context: RequestContext, review: TechnicalDocumentReview): Promise<{ allowed: boolean; reasons: string[] }> {
    const reasons: string[] = [];
    if (review.finalizedAt) reasons.push("O relatório já está finalizado.");
    if (review.status !== "PLH_APPROVED") reasons.push("O relatório ainda não foi aprovado pelo PLH.");
    if (this.corrections) {
      const pending = await this.corrections.listPendingByDocument(context, review.documentType, review.documentId);
      if (pending.length > 0) reasons.push(`existem ${pending.length} correção(ões) pendente(s)`);
    }
    if (this.holds) {
      const hold = await this.holds.activeHold(context, review.documentType, review.documentId);
      if (hold) reasons.push("o documento está EM HOLD");
    }
    return { allowed: reasons.length === 0, reasons };
  }

  /** Enfileirado por documento: ver `exclusive`. A regra vive em `finalizeLocked`. */
  async finalize(context: RequestContext, reviewId: string): Promise<TechnicalDocumentReview> {
    return this.exclusive(reviewId, () => this.finalizeLocked(context, reviewId));
  }

  private async finalizeLocked(context: RequestContext, reviewId: string): Promise<TechnicalDocumentReview> {
    this.auth.require(context, PermissionCode.EngineeringReviewView);
    const review = await this.requireReview(context, reviewId);
    const gate = await this.finalizationGate(context, review);
    if (!gate.allowed) throw new AppError(ErrorCode.Conflict, "Cannot finalize", { safeMessage: `Não é possível finalizar: ${gate.reasons.join("; ")}.` });
    const now = new Date().toISOString();
    const actor = await this.actorOf(context);
    const updated: TechnicalDocumentReview = { ...review, finalizedAt: now, finalizedBy: actor, updatedAt: now, history: [...review.history, { at: now, by: actor.userId, action: "FINALIZED", detail: `Relatório finalizado por ${actor.name} (Rev. ${review.revision}). ${review.signedAt ? "Documento assinado." : "Aguardando assinatura no módulo do documento."}`, professionalId: actor.professionalId }] };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.DocumentFinalized, updated, review.status, { finalizedBy: actor.userId, signed: !!review.signedAt, revision: review.revision });
    return updated;
  }

  // ------------------------------------------- SUBSTITUIÇÃO DO PLH

  /**
   * Documentos deste Cliente que AINDA dependem de atuação do PLH e que
   * podem, com segurança, passar para outro responsável.
   *
   * Reatribuíveis: aguardando análise, em análise e correção solicitada.
   *
   * NUNCA reatribuíveis:
   *   - `PLH_APPROVED` — a aprovação já foi feita, e por outra pessoa.
   *     Transferir faria o novo PLH constar como autor de um parecer que não
   *     é dele;
   *   - finalizados ou assinados — histórico imutável, com snapshot
   *     congelado.
   */
  async listReassignablePendingForClient(
    context: RequestContext,
    clientId: string,
    currentPlhProfessionalId?: string | null
  ): Promise<readonly TechnicalDocumentReview[]> {
    this.auth.require(context, PermissionCode.TechnicalReviewViewQueue);
    const reassignable: TechnicalReviewStatus[] = ["AWAITING_PLH_REVIEW", "PLH_REVIEWING", "CORRECTION_REQUESTED"];
    const all = await this.repo.listAll(context);
    return all.filter(review =>
      String(review.summary.clientId) === String(clientId)
      && reassignable.includes(review.status)
      && !review.finalizedAt
      && !review.signedAt
      && (!currentPlhProfessionalId || String(review.assignedPlhProfessionalId) === String(currentPlhProfessionalId))
    );
  }

  /**
   * Passa os pendentes do Cliente para o novo PLH.
   *
   * Um documento tem UM responsável atual — a reatribuição substitui, nunca
   * acrescenta uma segunda pendência. Quem pediu a correção continua no
   * histórico: a linha do tempo não é reescrita, só ganha o registro da
   * transferência.
   */
  async reassignPendingToPlh(
    context: RequestContext,
    clientId: string,
    newPlhProfessionalId: string,
    newPlhName: string,
    currentPlhProfessionalId?: string | null
  ): Promise<readonly TechnicalDocumentReview[]> {
    this.auth.require(context, PermissionCode.TechnicalReviewViewQueue);
    const pending = await this.listReassignablePendingForClient(context, clientId, currentPlhProfessionalId);
    const now = new Date().toISOString();
    const moved: TechnicalDocumentReview[] = [];

    for (const review of pending) {
      const previousPlhId = String(review.assignedPlhProfessionalId);
      const previousPlhName = review.assignedPlhName;
      if (previousPlhId === String(newPlhProfessionalId)) continue;

      const updated: TechnicalDocumentReview = {
        ...review,
        assignedPlhProfessionalId: newPlhProfessionalId,
        assignedPlhName: newPlhName,
        updatedAt: now,
        history: [...review.history, {
          at: now, by: context.userId || "system", action: "REASSIGNED" as never,
          detail: `Responsabilidade do PLH transferida de ${previousPlhName} para ${newPlhName} pela substituição do PLH do Cliente.`,
          professionalId: String(newPlhProfessionalId)
        }]
      };
      await this.repo.save(context, updated);
      moved.push(updated);

      await this.recordAudit(context, AuditAction.DocumentPlhReassigned, updated, review.status, {
        documentType: review.documentType, documentId: review.documentId, revision: review.revision,
        oldPlhProfessionalId: previousPlhId, newPlhProfessionalId: String(newPlhProfessionalId),
        status: review.status, reassignedBy: context.userId ?? null, reassignedAt: now
      });
    }
    return moved;
  }

  /** O módulo registra aqui a assinatura do documento (ato distinto da aprovação). */
  async markSigned(context: RequestContext, documentType: TechnicalDocumentType, documentId: string): Promise<void> {
    const review = await this.getDocumentReview(context, documentType, documentId);
    if (!review) return;
    const now = new Date().toISOString();
    const updated: TechnicalDocumentReview = { ...review, signedAt: now, updatedAt: now, history: [...review.history, { at: now, by: context.userId || "system", action: "SIGNED", detail: "Documento assinado." }] };
    await this.repo.save(context, updated);
    await this.recordAudit(context, AuditAction.DocumentPlhSigned, updated, review.status, {});
  }

  // ------------------------------------------------------------ QUEUE

  async listQueue(context: RequestContext, filters: TechnicalReviewQueueFilters = {}): Promise<TechnicalReviewQueueRow[]> {
    this.auth.require(context, PermissionCode.TechnicalReviewViewQueue);
    const rows: TechnicalReviewQueueRow[] = [];
    for (const review of await this.repo.listAll(context)) rows.push(this.rowOf(normalizeTechnicalReview(review)));
    for (const source of this.externalSources) {
      try { rows.push(...await source.listQueue(context)); } catch (e) { console.warn(`[REVISÃO PLH] fonte ${source.documentType} indisponível`, e); }
    }
    const q = (filters.search || "").trim().toLowerCase();
    return rows
      .filter(r => !context.activeClientId || r.clientId === String(context.activeClientId))
      .filter(r => !filters.documentType || filters.documentType === "ALL" || r.documentType === filters.documentType)
      .filter(r => !filters.plhProfessionalId || r.plhProfessionalId === filters.plhProfessionalId)
      .filter(r => !filters.clientId || r.clientId === filters.clientId)
      .filter(r => !filters.project || r.project === filters.project)
      .filter(r => !filters.unitArea || r.unitArea === filters.unitArea)
      .filter(r => !filters.status || filters.status === "ALL" || r.status === filters.status)
      // `from`/`to` vêm de um <input type="date">, que é calendário LOCAL.
      // `submittedAt.slice(0,10)` era o dia em UTC: um documento enviado às
      // 22h de 30/09 (BRT) contava como 01/10 e ficava fora do recorte do
      // próprio dia em que foi enviado. Os dois lados agora são locais.
      .filter(r => !filters.from || localDayOf(r.submittedAt) >= filters.from)
      .filter(r => !filters.to || localDayOf(r.submittedAt) <= filters.to)
      .filter(r => !q || [r.documentNumber, r.tag, r.osNumber, r.subOsNumber].some(v => (v || "").toLowerCase().includes(q)))
      .sort((a, b) => b.submittedAt.localeCompare(a.submittedAt));
  }

  rowOf(review: TechnicalDocumentReview): TechnicalReviewQueueRow {
    return {
      reviewId: review.id,
      documentType: review.documentType,
      typeLabel: TECHNICAL_DOCUMENT_TYPE_LABELS[review.documentType],
      documentId: review.documentId,
      documentNumber: review.summary.documentNumber,
      revision: review.revision,
      clientId: review.summary.clientId,
      client: review.summary.client,
      project: review.summary.project,
      unitArea: review.summary.unitArea,
      tag: review.summary.tag,
      osNumber: review.summary.osNumber,
      subOsNumber: review.summary.subOsNumber,
      elaboratedBy: review.submittedBy.name,
      engineerProfessionalId: review.responsibility?.engineerProfessionalId ?? null,
      engineerName: review.responsibility?.engineerName ?? "",
      finalizedAt: review.finalizedAt ?? null,
      inspectorName: review.summary.elaboratedBy || review.submittedBy.name,
      inspectorProfessionalId: review.summary.inspectorProfessionalId ?? review.submittedBy.professionalId,
      equipmentType: review.summary.equipmentType || "",
      inspectionDate: review.summary.inspectionDate || "",
      cycle: review.cycle,
      correctedAt: review.correctedAt,
      signedAt: review.signedAt,
      plhProfessionalId: review.assignedPlhProfessionalId,
      plhName: review.assignedPlhName,
      submittedAt: review.submittedAt,
      status: review.status,
      statusLabel: TECHNICAL_REVIEW_STATUS_LABELS[review.status],
      reviewRoute: `/engineering/review/${review.id}`,
      documentRoute: review.summary.route
    };
  }

  timelineOf(review: TechnicalDocumentReview): Array<{ at: string; label: string; detail: string; by: string }> {
    return review.history.map(h => ({ at: h.at, label: TECHNICAL_REVIEW_HISTORY_LABELS[h.action] || h.action, detail: h.detail, by: h.by }));
  }

  // ---------------------------------------------------------- HELPERS

  private async requireReview(context: RequestContext, id: string): Promise<TechnicalDocumentReview> {
    const review = await this.repo.findById(context, id);
    if (!review) throw new AppError(ErrorCode.NotFound, "Review not found", { safeMessage: "Análise não encontrada." });
    return normalizeTechnicalReview(review);
  }

  private async requireAssignedPlh(context: RequestContext, review: TechnicalDocumentReview): Promise<{ user: any; professional: any }> {
    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId) : null;
    // Habilitação pelo CLIENTE DO DOCUMENTO: quem o Cliente nomeou responsável.
    const eligibility = canActAsPlh(user, professional, await responsiblePlhIdOfClient(this.identityRepo as never, String(review.summary.clientId)));
    if (!eligibility.allowed) {
      throw new AppError(ErrorCode.Authorization, eligibility.reason || "Not a PLH", { safeMessage: eligibility.reason || "Você não está habilitado como PLH para este Cliente." });
    }
    if (String(review.assignedPlhProfessionalId) !== String(professional.id)) {
      throw new AppError(ErrorCode.Authorization, "Not the assigned PLH", { safeMessage: `Este documento está atribuído ao PLH ${review.assignedPlhName}. Somente ele pode analisá-lo.` });
    }
    return { user, professional };
  }

  private async actorOf(context: RequestContext): Promise<TechnicalReviewActor> {
    const userId = context.userId || "system";
    const user = this.identityRepo && context.userId ? await this.identityRepo.getUser(context.userId).catch(() => null) : null;
    const professional = this.identityRepo && user?.professionalId ? await this.identityRepo.getProfessional(user.professionalId).catch(() => null) : null;
    return { userId, professionalId: professional ? String(professional.id) : (user?.professionalId ? String(user.professionalId) : null), name: professional?.name || user?.name || userId };
  }

  private async userIdOfActor(actor: TechnicalReviewActor): Promise<string | null> {
    return actor.userId && actor.userId !== "system" ? actor.userId : null;
  }

  private async recordAudit(context: RequestContext, action: AuditAction, review: TechnicalDocumentReview, previousStatus: TechnicalReviewStatus | null, metadata: Record<string, unknown>): Promise<void> {
    if (!this.audit) return;
    try {
      await this.audit.record(context, {
        action,
        entityType: `TECHNICAL_REVIEW:${review.documentType}`,
        entityId: review.documentId,
        previousState: { status: previousStatus } as any,
        newState: { status: review.status } as any,
        metadata: { reviewId: review.id, documentType: review.documentType, documentId: review.documentId, documentNumber: review.summary.documentNumber, revision: review.revision, cycle: review.cycle, performedBy: context.userId || "system", professionalId: context.professionalId || null, plh: review.assignedPlhProfessionalId, at: review.updatedAt, previousStatus, newStatus: review.status, ...metadata } as any
      });
    } catch (e) {
      console.warn("[REVISÃO PLH] Falha ao registrar auditoria", e);
    }
  }

  private async notify(userId: string | null | undefined, event: any, title: string, message: string, review: TechnicalDocumentReview, route: string, idempotencyKey: string): Promise<void> {
    if (!userId) return;
    try {
      await notificationService.createNotification({ userId: String(userId), type: "INFO", event, title, message, entityType: "TechnicalReview", entityId: review.id, clientId: review.summary.clientId, route, idempotencyKey });
    } catch (e) {
      console.warn("[REVISÃO PLH] Falha ao notificar", e);
    }
  }
}
